import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import {
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Period, NewPeriod } from '../../../interfaces/period';
import { BehaviorSubject } from 'rxjs';
import {
  dayEndValidator,
  dayStartValidator,
  timeEndValidator,
  timeStartValidator,
} from '../../../shared/custom-validators';
import { ConfirmationDialogComponent } from '../../modals/confirm-dialog/confirmation-dialog.component';
import { filter } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';

type PeriodForm = {
  startDay: FormControl<number | null>;
  startTime: FormControl<string | null>;
  endDay: FormControl<number | null>;
  endTime: FormControl<string | null>;
  level?: FormControl<number | null>;
};

@UntilDestroy()
@Component({
  selector: 'app-period',
  templateUrl: './period.component.html',
  styleUrls: ['./period.component.scss']
})
export class PeriodComponent implements OnInit, OnChanges {

  @Input()
  public period!: Period;
  @Input()
  public periods!: Period[];
  @Input()
  public editable!: boolean;
  @Input()
  public periodType!: 'happy' | 'workingTime' | 'brightness';

  @Output()
  newPeriod = new EventEmitter<NewPeriod>();
  @Output()
  cancel = new EventEmitter<boolean>();
  @Output()
  delete = new EventEmitter<number>();
  @Output()
  update = new EventEmitter<Period>();
  @Output()
  isEdited = new EventEmitter<boolean>();

  public newPeriodForm!: FormGroup<PeriodForm>;
  public weekdays: {name: string, index: number}[] = [];
  public isEdit = new BehaviorSubject<boolean>(false);

  constructor(
    private translate: TranslateService,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.weekdays = [
      { index: 1, name: 'Monday' },
      { index: 2, name: 'Tuesday' },
      { index: 3, name: 'Wednesday' },
      { index: 4, name: 'Thursday' },
      { index: 5, name: 'Friday' },
      { index: 6, name: 'Saturday' },
      { index: 7, name: 'Sunday' }
    ];
    this.newPeriodForm = this.generateForm();

    if (this.period?.level) {
      this.newPeriodForm.patchValue({...this.period, level: Math.round(this.period?.level * 100)});
    } else {
      this.newPeriodForm.patchValue(this.period);
    }

    this.isEdit.pipe(
      untilDestroyed(this)
    ).subscribe((value) => {
      if (value && this.period) {
        this.newPeriodForm.enable();
      } else {
        this.newPeriodForm.disable();
      }
    });

    this.setupDateTimeFields();
  }

  private generateForm(): FormGroup<PeriodForm> {
    const form = new FormGroup<PeriodForm>({
      startDay: new FormControl({value: this.period?.startDay ?? null, disabled: true}, [
        Validators.required,
        dayStartValidator(this.periods, this.period?.id ?? null)
      ]),
      startTime: new FormControl({value: this.period?.startTime ?? null, disabled: true},  [
        Validators.required,
        timeStartValidator(this.periods, this.period?.id ?? null)
      ]),
      endDay: new FormControl({value: this.period?.endDay ?? null, disabled: true}, [
        Validators.required,
        dayEndValidator(this.periods, this.period?.id ?? null)
      ]),
      endTime: new FormControl({value: this.period?.endTime ?? null, disabled: true}, [
        Validators.required,
        timeEndValidator(this.periods, this.period?.id ?? null)
      ]),
    });

    if (this.periodType === 'brightness') {
      form.addControl(
        'level',
        new FormControl<number | null>(
          { value: this.period?.level ? Math.round(this.period?.level * 100) : null, disabled: false},
          [Validators.required, Validators.min(0), Validators.max(100)]
        )
      );
    }

    return form;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.periods) {
      this.updateValidators();
    }
  }

  private setupDateTimeFields(): void {
    const startDayControl = this.newPeriodForm.get('startDay');
    const startTimeControl = this.newPeriodForm.get('startTime');
    const endDayControl = this.newPeriodForm.get('endDay');
    const endTimeControl = this.newPeriodForm.get('endTime');
    const level = this.newPeriodForm.get('level');

    if (this.isEdit.getValue() || !this.period) {
      level?.enable();
    }

    if (!this.period) {
      startDayControl?.enable();
    }

    startDayControl?.valueChanges.pipe(
      untilDestroyed(this)
    ).subscribe(() => {
      if (startDayControl?.valid && (this.isEdit.getValue() || !this.period)) {
        startTimeControl?.enable();
      }
    });

    startTimeControl?.valueChanges.pipe(
      untilDestroyed(this)
    ).subscribe(() => {
      if (startTimeControl?.valid && (this.isEdit.getValue() || !this.period)) {
        endDayControl?.enable();
      }
    });

    endDayControl?.valueChanges.pipe(
      untilDestroyed(this)
    ).subscribe(() => {
      if (endDayControl?.valid && (this.isEdit.getValue() || !this.period)) {
        endTimeControl?.enable();
      }
    });
  }

  public createPeriod(): void {
    this.newPeriod.emit(this.getPeriodValue());
    this.newPeriodForm.reset();
  }

  public editPeriod(id: number): void {

    if (this.isEdit.getValue() && id === this.period.id) {
      const isFormChange = !this.areEqual(this.period, this.getPeriodValue());

      if (isFormChange) {
        const title = this.translate.instant('period.unsavedPeriodTitle');
        const message = this.translate.instant('period.unsavedChanges');
        const confirmationModal = this.dialog.open(ConfirmationDialogComponent, {
          data: {
            message,
            title
          },
          panelClass: 'confirmation-modal'
        });

        confirmationModal.afterClosed().pipe(
          filter(Boolean),
        ).subscribe((): void => {
          if (this.period?.level) {
            this.newPeriodForm.patchValue({...this.period, level: Math.round(this.period?.level * 100)});
          } else {
            this.newPeriodForm.patchValue(this.period);
          }
          this.isEdit.next(false);
          this.isEdited.emit(false);
        });
      } else {
        this.isEdit.next(false);
        this.isEdited.emit(false);
      }
    } else {
      this.isEdited.emit(true);
      this.isEdit.next(true);
    }
  }

  public deletePeriod(id: number): void {
    const title = this.translate.instant('period.' + this.periodType + '.deletePeriodTitle');
    const message = this.translate.instant('period.' + this.periodType + '.deletePeriod');

    const confirmationModal = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        message,
        title
      },
      panelClass: 'confirmation-modal'
    });

    confirmationModal.afterClosed().pipe(
      filter(Boolean),
    ).subscribe((): void => {
      this.delete.emit(id);
    });
  }

  public updatePeriod(id: number): void {
    this.update.emit({id, ...this.getPeriodValue()});
    this.isEdit.next(false);
    this.isEdited.emit(false);
  }

  public cancelCreation(): void {
    this.cancel.emit(false);
  }

  private areEqual = (a: Period, b: NewPeriod): boolean => {
    return (
      a.startDay === b.startDay &&
      a.startTime === b.startTime &&
      a.endDay === b.endDay &&
      a.endTime === b.endTime &&
      a.level === b.level
    );
  }

  private getPeriodValue(): NewPeriod {
    return this.newPeriodForm.getRawValue() as NewPeriod;
  }

  private updateValidators(): void {
    if (!this.newPeriodForm) {
      this.newPeriodForm = this.generateForm();
      return;
    }


    this.newPeriodForm.get('startDay')?.setValidators([
      Validators.required,
      dayStartValidator(this.periods, this.period?.id ?? null),
    ]);

    this.newPeriodForm.get('startTime')?.setValidators([
      Validators.required,
      timeStartValidator(this.periods, this.period?.id ?? null),
    ]);

    this.newPeriodForm.get('endDay')?.setValidators([
      Validators.required,
      dayEndValidator(this.periods, this.period?.id ?? null),
    ]);

    this.newPeriodForm.get('endTime')?.setValidators([
      Validators.required,
      timeEndValidator(this.periods, this.period?.id ?? null),
    ]);

    this.newPeriodForm.updateValueAndValidity();
  }

  public enforceMaxLength(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (input.value.length > 3) {
      input.value = input.value.slice(0, 3);

      const control = this.newPeriodForm.get('level');
      if (control) {
        control.setValue(Number(input.value), { emitEvent: false });
        control.updateValueAndValidity();
      }
    }
  }
}
